/**
 * First-party marketing analytics tracker.
 *
 * Sends lightweight session, pageview and conversion beacons to
 * /api/marketing-analytics.php and persists anonymous visitor/session
 * IDs in localStorage.
 *
 * Consent: reads localStorage.cookieConsent.analytics.
 * If the banner has not yet been shown (no cookieConsent key), beacons
 * fire by default — matching the existing GA4 / Clarity behaviour.
 */

const VISITOR_KEY      = 'ma_vid';
const SESSION_KEY      = 'ma_sid';
const SESSION_TS_KEY   = 'ma_sid_t';
const SESSION_TIMEOUT  = 30 * 60 * 1000; // 30 min idle

function getApiBase() {
  const env = process.env.REACT_APP_ENV;
  if (env === 'LOCAL')   return 'http://localhost:8888/Website/front-end/public/api';
  if (env === 'STAGING') return 'https://staging.belvoircastle.com/api';
  return 'https://www.belvoircastle.com/api';
}

function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem('cookieConsent');
    if (raw === null) return true; // banner not yet shown
    const consent = JSON.parse(raw);
    return consent?.analytics === true;
  } catch {
    return false;
  }
}

function generateId() {
  try {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function getDeviceType() {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getOrCreateVisitorId() {
  let vid = localStorage.getItem(VISITOR_KEY);
  if (!vid) {
    vid = generateId();
    try { localStorage.setItem(VISITOR_KEY, vid); } catch (_) {}
  }
  return vid;
}

function getOrCreateSessionId() {
  const lastTs  = parseInt(localStorage.getItem(SESSION_TS_KEY) || '0', 10);
  const now     = Date.now();
  const existing = localStorage.getItem(SESSION_KEY);

  if (existing && now - lastTs < SESSION_TIMEOUT) {
    try { localStorage.setItem(SESSION_TS_KEY, String(now)); } catch (_) {}
    return { sid: existing, isNew: false };
  }

  const sid = generateId();
  try {
    localStorage.setItem(SESSION_KEY, sid);
    localStorage.setItem(SESSION_TS_KEY, String(now));
  } catch (_) {}
  return { sid, isNew: true };
}

function beacon(data) {
  const url  = `${getApiBase()}/marketing-analytics.php`;
  const body = JSON.stringify(data);
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method:    'POST',
        body,
        headers:   { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {}
}

let _initialised = false;

/**
 * Call once on app mount. Writes visitor + session IDs and fires a session
 * beacon if this is a new session.
 */
export function initTracker() {
  if (_initialised) return;
  _initialised = true;

  if (!hasAnalyticsConsent()) return;

  const vid             = getOrCreateVisitorId();
  const { sid, isNew }  = getOrCreateSessionId();

  if (!isNew) return; // already fired for this session

  const params      = new URLSearchParams(window.location.search);

  beacon({
    action:       'session',
    visitor_id:   vid,
    session_id:   sid,
    landing_path: window.location.pathname,
    referrer:     document.referrer || '',
    utm_source:   params.get('utm_source')   || '',
    utm_medium:   params.get('utm_medium')   || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content:  params.get('utm_content')  || '',
    utm_term:     params.get('utm_term')     || '',
    device_type:  getDeviceType(),
  });
}

/**
 * Call on every route change.
 */
export function trackPageView(path) {
  if (!hasAnalyticsConsent()) return;
  const sid = localStorage.getItem(SESSION_KEY);
  if (!sid) return;
  try { localStorage.setItem(SESSION_TS_KEY, String(Date.now())); } catch (_) {}
  beacon({
    action:     'pageview',
    session_id: sid,
    path:       path || window.location.pathname,
  });
}

/**
 * Call after a confirmed booking.
 *
 * @param {object} params
 * @param {'ticket_booking'|'membership_signup'} params.type
 * @param {string}  params.bookingReference
 * @param {number}  params.value
 * @param {string}  [params.currency]
 * @param {string}  [params.locationId]
 * @param {string}  [params.pageId]
 * @param {Array}   [params.items]
 */
export function trackConversion({ type, bookingReference, value, currency = 'GBP', locationId, pageId, items }) {
  if (!hasAnalyticsConsent()) return;
  const vid = localStorage.getItem(VISITOR_KEY) || '';
  const sid = localStorage.getItem(SESSION_KEY)  || '';
  beacon({
    action:            'conversion',
    visitor_id:        vid,
    session_id:        sid,
    type,
    booking_reference: bookingReference,
    value,
    currency,
    location_id:       locationId,
    page_id:           pageId,
    items,
  });
}
